import { apiClient } from './client';
import type {
  StockLevel,
  StockMovement,
  InventoryCheck,
  InventoryCheckItem,
  DiscrepancyReport,
  DeliverySheet,
} from '@/types/models';

interface StockLevelListResponse {
  data: StockLevel[];
}

interface PaginatedMovementsResponse {
  data: StockMovement[];
  count: number;
  page: number;
  limit: number;
}

interface PaginatedDeliverySheetsResponse {
  data: DeliverySheet[];
  count: number;
  page: number;
  limit: number;
}

export const inventoryApi = {
  getStock: async (productId?: number): Promise<StockLevelListResponse> => {
    const params: Record<string, unknown> = {};
    if (productId !== undefined) params.product_id = productId;

    const response = await apiClient.get<StockLevelListResponse>('/v1/inventory/stock', { params });
    return response.data;
  },

  getMovements: async (filters?: {
    product_id?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedMovementsResponse> => {
    const response = await apiClient.get<PaginatedMovementsResponse>('/v1/inventory/movements', {
      params: filters,
    });
    return response.data;
  },

  startCheck: async (notes?: string): Promise<InventoryCheck> => {
    const response = await apiClient.post<InventoryCheck>(
      '/v1/inventory/checks',
      notes ? { notes } : undefined,
    );
    return response.data;
  },

  recordCheckItem: async (checkId: number, data: {
    product_id: number;
    actual_quantity: number;
    notes?: string;
  }): Promise<InventoryCheckItem> => {
    const response = await apiClient.post<InventoryCheckItem>(
      `/v1/inventory/checks/${checkId}/items`,
      data,
    );
    return response.data;
  },

  completeCheck: async (checkId: number): Promise<InventoryCheck> => {
    const response = await apiClient.post<InventoryCheck>(
      `/v1/inventory/checks/${checkId}/complete`,
    );
    return response.data;
  },

  getCheckReport: async (checkId: number): Promise<DiscrepancyReport> => {
    const response = await apiClient.get<DiscrepancyReport>(
      `/v1/inventory/checks/${checkId}/report`,
    );
    return response.data;
  },

  listDeliverySheets: async (filters?: {
    delivery_id?: number;
    page?: number;
    limit?: number;
  }): Promise<PaginatedDeliverySheetsResponse> => {
    const response = await apiClient.get<PaginatedDeliverySheetsResponse>(
      '/v1/inventory/delivery-sheets',
      { params: filters },
    );
    return response.data;
  },

  getDeliverySheet: async (sheetId: number): Promise<DeliverySheet> => {
    const response = await apiClient.get<DeliverySheet>(
      `/v1/inventory/delivery-sheets/${sheetId}`,
    );
    return response.data;
  },
};
